import React from 'react'
import {classNames} from "js/studios/shared/utils"

/**
 * Generic paginator which requires these props:
 *
 * numOfPages -> number of pages in results
 * currentPage -> current page you're on
 * onPageChange -> callback for pagination click
 */
export class PaginatorNav extends React.Component {
  pageChange(page) {
    this.props.onPageChange(page)
  }

  nextPageClick = (e) => {
    e.preventDefault()
    const nextPage = this.props.currentPage + 1
    if (nextPage <= this.props.numOfPages) {
      this.pageChange(nextPage)
    }
  }

  previousPageClick = (e) => {
    e.preventDefault()
    const previousPage = this.props.currentPage - 1
    if (previousPage >= 1) {
      this.pageChange(previousPage)
    }
  }

  onPageLinkClick = (page, e) => {
    e.preventDefault()
    this.pageChange(page)
  }

  createPageUrl(page){
    if (this.props.createPageUrl) {
      return this.props.createPageUrl(page)
    }
    return window.location.pathname + "?page=" + page
  }

  createPageLink(page) {
    return <li key={page} className={classNames("pagination-item", {current: page === this.props.currentPage})}>
      <a href={this.createPageUrl(page)} onClick={e => this.onPageLinkClick(page, e)} data-track-event-sp={"click_pagination*browse*page_selected=" + page}>{page}</a>
    </li>
  }

  createEllipses() {
    return <li key={Math.random()} className="pagination-item ellipsis">...</li>
  }

  /**
   * We're relatively smart here..
   * - if we're in the first 4 pages, we show the first 5 elements and the last element
   * - If we're in the last 5 pages, we show the first element and the last 5 pages
   * - If we're in between, we show the first & last element and one page on either side of the current page
   * @return {list of li's -- some links, some ellipses}
   */
  getPageLinks() {
    const currentPage = this.props.currentPage,
        numOfPages = this.props.numOfPages,
        pageLinks = []

    if (currentPage < 5) {
      for (let i = 1; i <= 5 && i <= numOfPages; i++) {
        pageLinks.push(this.createPageLink(i))
      }

      if (numOfPages > 5) {
        pageLinks.push(this.createEllipses())
        pageLinks.push(this.createPageLink(numOfPages))
      }
    } else if (currentPage > numOfPages - 5) {
      pageLinks.push(this.createPageLink(1))
      pageLinks.push(this.createEllipses())
      for (let i = numOfPages - 5; i <= numOfPages; i++) {
        pageLinks.push(this.createPageLink(i))
      }
    } else {
      pageLinks.push(this.createPageLink(1))
      pageLinks.push(this.createEllipses())
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pageLinks.push(this.createPageLink(i))
      }

      if (currentPage + 1 < numOfPages) {
        pageLinks.push(this.createEllipses())
        pageLinks.push(this.createPageLink(numOfPages))
      }
    }

    return pageLinks
  }

  render() {
    const numOfPages = this.props.numOfPages,
        currentPage = this.props.currentPage,
        id = this.props.id || ""
    return <section id={id} className="studios-pagination">
      <div className="pagination-wrapper">
        <a className={classNames("previous-page", {inactive: currentPage === 1})} onClick={this.previousPageClick} data-track-event-sp="click_pagination*browse*page_selected=previous"></a>
        <ul className="pagination-set">
          {this.getPageLinks()}
        </ul>
        <a className={classNames("next-page", {inactive: currentPage === numOfPages})} onClick={this.nextPageClick} data-track-event-sp="click_pagination*browse*page_selected=next"></a>
      </div>
    </section>
  }
}

/**
 * A paginator element for when you don't know exactly how many pages there are.
 *
 * onPageChange: callback for pagination click
 * currentPage: current page being displayed
 * hasNext: boolean, optional and assumed true if not present
 */
export function RelativePaginatorNav({currentPage, onPageChange, hasNext}) {
  if (hasNext === undefined) {
    hasNext = true
  }

  const
    handlePrev = (ev) => {
      ev.preventDefault()
      if (currentPage > 1) { onPageChange(currentPage - 1) }
    },
    handleNext = (ev) => {
      ev.preventDefault()
      if (hasNext) { onPageChange(currentPage + 1) }
    }

  return <section className="studios-pagination">
    <div className="pagination-wrapper">
      <ul className="pagination-set">
        <li className={classNames("pagination-item", {disabled: currentPage <= 1})}>
          <a href="" onClick={handlePrev} title="Previous page" data-track-event-sp="click_pagination*browse*page_selected=previous">&larr;</a>
        </li>
        <li className="pagination-item current">
          <a href="" onClick={e => e.preventDefault()}>{currentPage}</a>
        </li>
        <li className={classNames("pagination-item", {disabled: !hasNext})}>
          <a href="" onClick={handleNext} title="Next page" data-track-event-sp="click_pagination*browse*page_selected=next">&rarr;</a>
        </li>
      </ul>
    </div>
  </section>
}
